<script setup>
import { onMounted, computed, ref, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import ContactEmptyState from 'dashboard/components-next/Contacts/EmptyState/ContactEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactsBulkActionBar from '../components/ContactsBulkActionBar.vue';
import ContactsFilterBar from '../components/ContactsFilterBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';
import ContactAPI from 'dashboard/api/contacts';

const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const contacts = useMapGetter('contacts/getContactsList');
// 所有联系人(index)只显示有联系方式的; 消息记录(active)只显示发过消息的(hasMessages),
// 排除 websocket 推送进来的静默联系人(后端 _contact.json.jbuilder 输出 has_messages)
const filteredContacts = computed(() => {
  if (route.name === 'contacts_dashboard_index') {
    // 所有联系人: 留了电话/邮箱/微信/固话/QQ 任一, 或标记未识别已同步 即显示(与后端可见性过滤口径一致)
    return contacts.value.filter(
      c =>
        c.phoneNumber ||
        c.phone_number ||
        c.customAttributes?.phone ||
        c.email ||
        c.customAttributes?.wechat ||
        c.customAttributes?.landline ||
        c.customAttributes?.qq ||
        c.customAttributes?.card_status === '未识别已同步'
    );
  }
  return contacts.value.filter(c => c.hasMessages);
});
const uiFlags = useMapGetter('contacts/getUIFlags');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);
// For infinite scroll in search, track page internally
const searchPageNumber = ref(1);
const isLoadingMore = ref(false);

// 定制: 名片管理每页条数(10/20/50/100), 持久化到 localStorage, 默认 15
const CONTACTS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const readStoredPerPage = () => {
  const stored = Number(window.localStorage?.getItem('cw_contacts_per_page'));
  return CONTACTS_PER_PAGE_OPTIONS.includes(stored) ? stored : 15;
};
const contactsPerPage = ref(readStoredPerPage());

// 定制: 默认排序按路由区分。用户没手动设过排序时:
//   消息记录(active) → 按最近活跃(说话时间)倒序, 最近说话的在最前
//   其他视图         → 按名片保存时间(留电时间)倒序
const defaultSortForCurrentRoute = () =>
  route.name === 'contacts_dashboard_active'
    ? { sort: 'last_activity_at', order: '-' }
    : { sort: 'custom_attributes.contact_saved_at', order: '-' };

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField,
    order: hasDescending ? '-' : '',
  };
};

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const parsedSort = contactSortBy
  ? parseSortSettings(contactSortBy)
  : defaultSortForCurrentRoute();

const sortState = reactive({
  activeSort: parsedSort.sort,
  activeOrdering: parsedSort.order,
});

// 切路由时,若用户没手动设过排序,按路由默认重排(进消息记录→最近活跃在前)
watch(
  () => route.name,
  () => {
    if (uiSettings.value?.contacts_sort_by) return;
    const d = defaultSortForCurrentRoute();
    sortState.activeSort = d.sort;
    sortState.activeOrdering = d.order;
  }
);

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const isFetchingList = computed(
  () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);
const hasMore = computed(() => meta.value?.hasMore ?? false);
const isSearchView = computed(() => !!searchQuery.value);

const selectedContactIds = ref([]);
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);
const selectedCount = computed(() => selectedContactIds.value.length);
const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);
const hasSelection = computed(() => selectedCount.value > 0);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasContacts = computed(() => contacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});

const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CONTACTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchQuery.value || hasAppliedFilters.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const visibleContactIds = computed(() =>
  contacts.value.map(contact => contact.id)
);

const clearSelection = () => {
  selectedContactIds.value = [];
};

const openBulkDeleteDialog = () => {
  if (!selectedContactIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  const currentSelection = new Set(selectedContactIds.value);
  if (shouldSelect) {
    visibleContactIds.value.forEach(id => currentSelection.add(id));
  } else {
    visibleContactIds.value.forEach(id => currentSelection.delete(id));
  }
  selectedContactIds.value = Array.from(currentSelection);
};

const toggleContactSelection = ({ id, value }) => {
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }
};

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

// 定制: 默认筛选"今天"(按北京时区), 与 FilterBar 默认显示一致。
// 否则 contactFilters 初值空 → 首次 fetch 不带日期 → 显示全部(非今天)。
const _bjNow = new Date(
  new Date().getTime() + new Date().getTimezoneOffset() * 60000 + 8 * 3600000
);
const _padD = n => String(n).padStart(2, '0');
const _todayStr = `${_bjNow.getFullYear()}-${_padD(_bjNow.getMonth() + 1)}-${_padD(
  _bjNow.getDate()
)}`;
const contactFilters = ref({
  created_after: `${_todayStr} 00:00:00`,
  created_before: `${_todayStr} 23:59:59`,
});

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
  filters: contactFilters.value,
  perPage: contactsPerPage.value,
});

const applyContactFilters = filters => {
  contactFilters.value = filters || {};
  pageNumber.value = 1;
  if (isActiveView.value) {
    fetchActiveContacts(1, { clearSelection: false });
  } else {
    fetchContacts(1);
  }
};

const resetContactFilters = () => {
  contactFilters.value = {};
  pageNumber.value = 1;
  if (isActiveView.value) {
    fetchActiveContacts(1, { clearSelection: false });
  } else {
    fetchContacts(1);
  }
};

// 名片右侧抽屉: 点击名片不跳页, 右侧展开 ContactDetails
const contactById = useMapGetter('contacts/getContactById');
const panelContactId = ref(null);
const panelContact = computed(() =>
  panelContactId.value ? contactById.value(panelContactId.value) : null
);
// 抽屉选项卡: 详情 / 消息记录
const activeTab = ref('details');
const contactConversations = ref([]);
const selectedConvId = ref(null);
const loadingConversations = ref(false);
const loadConversations = async () => {
  if (!panelContactId.value) return;
  loadingConversations.value = true;
  try {
    const { data } = await ContactAPI.getConversations(panelContactId.value);
    contactConversations.value = data?.payload || [];
    selectedConvId.value = contactConversations.value[0]?.id || null;
  } catch (e) {
    contactConversations.value = [];
    selectedConvId.value = null;
  } finally {
    loadingConversations.value = false;
  }
};
const switchTab = async tab => {
  activeTab.value = tab;
  if (tab === 'messages' && !contactConversations.value.length) {
    await loadConversations();
  }
};
const goToConversation = conversationId => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: conversationId,
    },
  });
  closeContactPanel();
};
// 复制对话记录H5链接(可推送到CRM)
const copyChatRecordLink = conversationId => {
  const url = `${window.location.origin}/chat_records/${conversationId}`;
  navigator.clipboard
    ?.writeText(url)
    .then(() => useAlert(`已复制链接: ${url}`))
    .catch(() => useAlert(url));
};
const conversationStatusText = status => {
  const map = {
    open: '开放',
    resolved: '已解决',
    pending: '待处理',
    bot: '机器人',
  };
  return map[status] || status || '';
};
const conversationLastMessage = conv => {
  const msgs = conv?.messages || [];
  const last = [...msgs]
    .reverse()
    .find(m => m.content && (m.message_type === 0 || m.message_type === 1));
  return last?.content || '';
};
const openContactPanel = async id => {
  panelContactId.value = id;
  activeTab.value = 'details';
  contactConversations.value = [];
  await store.dispatch('contacts/show', { id });
};
const closeContactPanel = () => {
  panelContactId.value = null;
};

const fetchContacts = async (page = 1, options = {}) => {
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/get', getCommonFetchParams(page));
  updatePageParam(page);
};

const fetchSavedOrAppliedFilteredContact = async (
  payload,
  page = 1,
  options = {}
) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;

  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }

  await store.dispatch('contacts/filter', {
    ...getCommonFetchParams(page),
    queryPayload: payload,
  });
  updatePageParam(page);
};

const fetchActiveContacts = async (page = 1, options = {}) => {
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }

  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/active', {
    page,
    sortAttr: buildSortAttr(),
    perPage: contactsPerPage.value,
    filters: contactFilters.value,
  });
  updatePageParam(page);
};

const searchContacts = debounce(
  async (value, page = 1, append = false, options = {}) => {
    const { clearSelection: shouldClearSelection = true } = options;

    if (!append) {
      searchPageNumber.value = 1;

      if (shouldClearSelection) {
        clearSelection();
      }
    }
    await store.dispatch('contacts/clearContactFilters');
    searchValue.value = value;

    if (!value) {
      updatePageParam(page);
      await fetchContacts(page, { clearSelection: false });
      return;
    }

    updatePageParam(page, value);
    await store.dispatch('contacts/search', {
      ...getCommonFetchParams(page),
      search: encodeURIComponent(value),
      append,
    });
    searchPageNumber.value = page;
  },
  DEBOUNCE_DELAY
);

const loadMoreSearchResults = async () => {
  if (!hasMore.value || isLoadingMore.value) return;

  isLoadingMore.value = true;
  const nextPage = searchPageNumber.value + 1;

  await store.dispatch('contacts/search', {
    ...getCommonFetchParams(nextPage),
    search: encodeURIComponent(searchValue.value),
    append: true,
  });

  searchPageNumber.value = nextPage;
  isLoadingMore.value = false;
};

const fetchContactsBasedOnContext = async (page, options = {}) => {
  const { clearSelection: shouldClearSelection = true } = options;
  if (shouldClearSelection) {
    clearSelection();
  }
  updatePageParam(page, searchValue.value);
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchContacts(searchQuery.value, page, false, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If we're on the active route, fetch active contacts
  if (isActiveView.value) {
    await fetchActiveContacts(page, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredContact(queryPayload, page, {
      clearSelection: shouldClearSelection,
    });
    return;
  }
  // Default case: fetch regular contacts + label
  await fetchContacts(page, {
    clearSelection: shouldClearSelection,
  });
};

const onPageChange = page =>
  fetchContactsBasedOnContext(page, { clearSelection: false });

// 定制: 切换每页条数 → 持久化 + 回到第 1 页重新拉取
const onPerPageChange = perPage => {
  if (!CONTACTS_PER_PAGE_OPTIONS.includes(Number(perPage))) return;
  contactsPerPage.value = Number(perPage);
  try {
    window.localStorage.setItem('cw_contacts_per_page', String(perPage));
  } catch {
    // localStorage 不可用时静默忽略
  }
  fetchContactsBasedOnContext(1, { clearSelection: false });
};

const assignLabels = async labels => {
  if (!labels.length || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: labels },
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const removeLabels = async labels => {
  if (!labels.length || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { remove: labels },
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.REMOVE_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteContacts = async () => {
  if (!selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      action_name: 'delete',
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
    bulkDeleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchContacts(searchValue.value, pageNumber.value, false, {
      clearSelection: false,
    });
    return;
  }

  if (isActiveView.value) {
    await fetchActiveContacts();
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredContact(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchContacts());
};

const createContact = async contact => {
  await store.dispatch('contacts/create', contact);
};

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  () => {
    fetchContactsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchContacts();
  }
});

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchContacts(searchQuery.value, pageNumber.value, false, {
        clearSelection: false,
      });
      return;
    }
    if (isActiveView.value) {
      await fetchActiveContacts(pageNumber.value);
      return;
    }
    await fetchContacts(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredContact(
      activeSegment.value.query,
      pageNumber.value
    );
  }
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <ContactsListLayout
      :search-value="searchValue"
      :header-title="headerTitle"
      :current-page="currentPage"
      :total-items="totalItems"
      :items-per-page="contactsPerPage"
      :show-pagination-footer="!isFetchingList && hasContacts && !isSearchView"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :active-segment="activeSegment"
      :segments-id="activeSegmentId"
      :is-fetching-list="isFetchingList"
      :has-applied-filters="hasAppliedFilters"
      :use-infinite-scroll="isSearchView"
      :has-more="hasMore"
      :is-loading-more="isLoadingMore"
      @update:current-page="onPageChange"
      @update:items-per-page="onPerPageChange"
      @search="
        value => searchContacts(value, 1, false, { clearSelection: false })
      "
      @update:sort="handleSort"
      @apply-filter="fetchSavedOrAppliedFilteredContact"
      @clear-filters="fetchContacts"
      @load-more="loadMoreSearchResults"
    >
      <ContactsFilterBar
        v-if="!isSearchView"
        @apply="applyContactFilters"
        @reset="resetContactFilters"
      />
      <div
        v-if="isFetchingList && !(isSearchView && hasContacts)"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <ContactsBulkActionBar
          v-if="hasSelection"
          :visible-contact-ids="visibleContactIds"
          :selected-contact-ids="selectedContactIds"
          :is-loading="isBulkActionLoading"
          @toggle-all="toggleSelectAll"
          @clear-selection="clearSelection"
          @assign-labels="assignLabels"
          @remove-labels="removeLabels"
          @delete-selected="openBulkDeleteDialog"
        />
        <ContactEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('CONTACTS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('CONTACTS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('CONTACTS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
          @create="createContact"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4 pt-4 pb-6">
          <ContactsList
            :contacts="filteredContacts"
            :current-page="currentPage"
            :per-page="contactsPerPage"
            :highlight-contact-info="isActiveView"
            :selected-contact-ids="selectedContactIds"
            @toggle-contact="toggleContactSelection"
            @view-details="openContactPanel"
          />
          <Dialog
            v-if="selectedCount"
            ref="bulkDeleteDialogRef"
            type="alert"
            :title="bulkDeleteDialogTitle"
            :description="bulkDeleteDialogDescription"
            :confirm-button-label="bulkDeleteDialogConfirmLabel"
            :is-loading="isBulkActionLoading"
            @confirm="deleteContacts"
          />
        </div>
      </template>
    </ContactsListLayout>

    <!-- 名片右侧抽屉: 点击名片不跳页, 右侧展开 -->
    <div v-if="panelContactId" class="fixed inset-0 z-50 flex justify-end">
      <div
        class="absolute inset-0 bg-n-slate-12/40"
        @click="closeContactPanel"
      />
      <aside
        class="relative h-full w-full max-w-[460px] bg-n-surface-1 shadow-2xl border-l border-n-slate-4 flex flex-col"
      >
        <header
          class="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-n-slate-4"
        >
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ panelContact?.name || '' }}
          </span>
          <button
            class="flex-shrink-0 text-n-slate-11 hover:text-n-slate-12"
            @click="closeContactPanel"
          >
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </header>
        <div
          class="flex items-center gap-1 flex-shrink-0 px-4 py-2 border-b border-n-slate-4"
        >
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              activeTab === 'details'
                ? 'bg-n-alpha-2 text-n-slate-12'
                : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="switchTab('details')"
          >
            详情
          </button>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              activeTab === 'messages'
                ? 'bg-n-alpha-2 text-n-slate-12'
                : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="switchTab('messages')"
          >
            消息记录
          </button>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-5">
          <ContactDetails
            v-if="panelContact && activeTab === 'details'"
            :selected-contact="panelContact"
            @go-to-contacts-list="closeContactPanel"
          />
          <div v-else-if="activeTab === 'messages'" class="flex flex-col h-full">
            <div
              v-if="loadingConversations"
              class="flex items-center justify-center py-10 text-n-slate-11"
            >
              <Spinner />
            </div>
            <div
              v-else-if="!contactConversations.length"
              class="text-center py-10 text-n-slate-11 text-sm"
            >
              暂无对话记录
            </div>
            <template v-else>
              <div
                class="flex items-center gap-2 mb-3 pb-2 border-b border-n-slate-4"
              >
                <select
                  v-if="contactConversations.length > 1"
                  v-model="selectedConvId"
                  class="text-xs border border-n-slate-4 rounded px-2 py-1 bg-transparent"
                >
                  <option
                    v-for="conv in contactConversations"
                    :key="conv.id"
                    :value="conv.id"
                  >
                    会话#{{ conv.id }} ({{ conversationStatusText(conv.status) }})
                  </option>
                </select>
                <span v-else class="text-xs text-n-slate-11"
                  >会话#{{ selectedConvId }}</span
                >
                <button
                  v-if="selectedConvId"
                  class="ml-auto text-xs text-n-blue-11 hover:text-n-blue-9"
                  @click="copyChatRecordLink(selectedConvId)"
                >
                  复制链接
                </button>
              </div>
              <iframe
                v-if="selectedConvId"
                :src="`/chat_records/${selectedConvId}`"
                class="w-full flex-1"
                style="min-height: 500px; border: 0"
                frameborder="0"
              ></iframe>
            </template>
          </div>
          <div
            v-else
            class="flex items-center justify-center py-10 text-n-slate-11"
          >
            <Spinner />
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>
